import i18n from 'i18next';
import {
    isEqual,
    isNumber,
    isPlainObject,
    isString,
    snakeCase,
    startCase
} from 'lodash';
import { validate as isUUID } from 'uuid';
import constants from '../constants';
import { appVersion } from '~/version';
import type { Coordinates, CoordinatePoint } from '~/api/types/CommonTypes';
import type { Polygon as GeoJsonPolygon } from 'geojson';

/**
 * General Utilities
 *
 * @category Utils
 * @module utils/generalUtils
 *
 * @example
 * import generalUtils from '~/utils/general-utils';
 */

export const getAppVersion = () => {
    return appVersion;
};

/**
 * Converts a given string to title case
 *
 * Relies on lodash's `startCase` function to do the conversion.
 * This will strip out special characters, leaving only alphanumeric characters.
 */
export const stringToTitle = (str: string) => {
    return startCase(snakeCase(str));
};

/**
 * Get the capitalized first letter of a string
 *
 * @param {String} str
 * @returns {string}
 */
function getCapitalizedFirstChar(str: string): string {
    return isString(str) ? str.charAt(0).toUpperCase() : '';
}

function capitalizeFirstChar(str: string): string {
    if (!str) {
        return '';
    }
    return getCapitalizedFirstChar(str) + str.slice(1);
}

/**
 * Return user's localized full name, if unavailable, returns alternative
 *
 * @param {String} firstName
 * @param {String} lastName
 * @param {String} alternative
 * @returns {String} full name or alternative
 */
function getUserName(
    firstName: string,
    lastName: string,
    alternative = ''
): string {
    if (!firstName && !lastName) {
        return alternative;
    }
    return i18n.t('common:name.full', { firstName, lastName }).trim();
}

/**
 * Return user's localized initials, if unavailable, returns alternative
 *
 * @param {String} firstName
 * @param {String} lastName
 * @param {String} alternative
 * @returns {String} initials or alternative
 */
function getUserInitials(
    firstName: string,
    lastName: string,
    alternative = ''
): string {
    if (!firstName && !lastName) {
        return alternative;
    }
    const firstInitial = getCapitalizedFirstChar(firstName);
    const lastInitial = getCapitalizedFirstChar(lastName);
    return i18n
        .t('common:name.initials', { firstInitial, lastInitial })
        ?.trim();
}

export const getInitials = (name: string, delimiter = '') => {
    return name
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0))
        .join(delimiter)
        .toUpperCase();
};

export const getCurrentHost = () => {
    const { hostname } = window.location;
    const isLocal = /localhost/.test(hostname) || /127\.0\.0\.1/.test(hostname);
    const isStage = /admin\.stage\.wisesys\.info/.test(hostname);
    const isSandbox =
        /admin\.sandbox\.wisesys\.info/.test(hostname) ||
        /wise-web-multi-client-mrs\.wisesys\.info/.test(hostname);
    const isDemo = /admin\.demo\.wisesys\.info/.test(hostname);
    const isUAT = /admin\.uat\.wisesys\.info/.test(hostname);

    return {
        hostname,
        isLocal,
        isStage,
        isSandbox,
        isDemo,
        isUAT
    };
};

/**
 * Get the `toggle` value of a specified localStorage key
 *
 * @param localStorageKey the localStorage key to retrieve
 * @returns `boolean`: whether the localStorage key was explicitly set with `true` or `false`
 * @returns `null`: whether the localStorage key was not found or was set with an invalid value
 */
export const getLocalStorageToggle = (
    localStorageKey: string
): boolean | null => {
    if (!localStorageKey) return null;

    const localStorageValue = localStorage.getItem(localStorageKey) || '';
    const localStorageValueLowercase = localStorageValue.toLowerCase();
    const isValid = ['true', 'false'].includes(localStorageValueLowercase);

    return isValid ? localStorageValueLowercase === 'true' : null;
};

/**
 * Detect if window.location.hostname is for demo (admin.demo.wisesys.info)
 *
 * @returns {Boolean}
 */
function isCurrentHostDemo(): boolean {
    const { isDemo } = getCurrentHost();

    return isDemo;
}

/**
 * Detect if window.location.hostname is for uat (admin.uat.wisesys.info)
 *
 * @returns {Boolean}
 */
function isCurrentHostUAT(): boolean {
    const { isUAT } = getCurrentHost();

    return isUAT;
}

/**
 * Tests if the provided string is a valid uuid string
 *
 * @example
 * // tests against a valid UUID string `54b8f1d7-c50a-4ab6-b2d5-c36ee6bb9053`
 * isValidUUID('54b8f1d7-c50a-4ab6-b2d5-c36ee6bb9053')
 * @returns {Boolean} true if uuidValue is valid
 */
export const isValidUUID = (uuidValue: unknown): uuidValue is string => {
    return isString(uuidValue) && isUUID(uuidValue);
};

/**
 * Tests if the provided value is a valid latitude (-90 to 90 degrees, inclusive)
 *
 * @example
 * isValidLatitude(45.5) // true
 * isValidLatitude(91) // false
 * @returns {Boolean} true if latitude is valid
 */
export const isValidLatitude = (latitude: unknown): latitude is number => {
    return isNumber(latitude) && latitude >= -90 && latitude <= 90;
};

/**
 * Tests if the provided value is a valid longitude (-180 to 180 degrees, inclusive)
 *
 * @example
 * isValidLongitude(120.5) // true
 * isValidLongitude(181) // false
 * @returns {Boolean} true if longitude is valid
 */
export const isValidLongitude = (longitude: unknown): longitude is number => {
    return isNumber(longitude) && longitude >= -180 && longitude <= 180;
};

/**
 * Tests if the provided value is a valid `Coordinates` object (`{ lat, lng }`)
 * with a valid latitude and longitude
 *
 * @example
 * isValidCoordinate({ lat: 45.5, lng: 120.5 }) // true
 * isValidCoordinate({ lat: 91, lng: 120.5 }) // false
 * @returns {Boolean} true if coordinate is valid
 */
export const isValidCoordinate = (
    coordinate: unknown
): coordinate is Coordinates => {
    const { lat, lng } = (coordinate as Coordinates) ?? {};
    return isValidLatitude(lat) && isValidLongitude(lng);
};

/**
 * Tests if the provided value is a flat path of valid `Coordinates` objects
 *
 * @example
 * isCoordinatesPath([{ lat: 45.5, lng: 120.5 }]) // true
 * isCoordinatesPath([[{ lat: 45.5, lng: 120.5 }]]) // false
 * @returns {Boolean} true if value is a `Coordinates[]`
 */
export const isCoordinatesPath = (value: unknown): value is Coordinates[] => {
    return Array.isArray(value) && value.every(isValidCoordinate);
};

/**
 * Tests if the provided value is a nested path (a list of rings) of valid
 * `Coordinates` objects
 *
 * @example
 * isNestedCoordinatesPath([[{ lat: 45.5, lng: 120.5 }]]) // true
 * isNestedCoordinatesPath([{ lat: 45.5, lng: 120.5 }]) // false
 * @returns {Boolean} true if value is a `Coordinates[][]`
 */
export const isNestedCoordinatesPath = (
    value: unknown
): value is Coordinates[][] => {
    return Array.isArray(value) && value.every(isCoordinatesPath);
};

/**
 * Tests if the provided value is a valid polygon path, either flat
 * (`Coordinates[]`) or nested (`Coordinates[][]`)
 *
 * @example
 * isValidPolygonPaths([{ lat: 45.5, lng: 120.5 }]) // true
 * isValidPolygonPaths([[{ lat: 45.5, lng: 120.5 }]]) // true
 * isValidPolygonPaths([{ lat: 91, lng: 120.5 }]) // false
 * @returns {Boolean} true if value is a `Coordinates[]` or `Coordinates[][]`
 */
export const isValidPolygonPaths = (
    polygonPaths: unknown
): polygonPaths is Coordinates[] | Coordinates[][] => {
    return (
        isCoordinatesPath(polygonPaths) || isNestedCoordinatesPath(polygonPaths)
    );
};

const isValidGeoJsonPosition = (
    position: unknown
): position is CoordinatePoint => {
    if (!Array.isArray(position) || position.length !== 2) {
        return false;
    }
    const [lng, lat] = position;
    return isValidLongitude(lng) && isValidLatitude(lat);
};

const isValidGeoJsonLinearRing = (ring: unknown): ring is CoordinatePoint[] =>
    Array.isArray(ring) &&
    ring.length >= 4 &&
    ring.every(isValidGeoJsonPosition) &&
    isEqual(ring[0], ring[ring.length - 1]);

/**
 * Tests if the provided value conforms to the GeoJSON `Polygon` type
 * (`{ type: 'Polygon', coordinates: CoordinatePoint[][] }`)
 *
 * @returns {Boolean} true if value is a valid GeoJSON Polygon
 */
export const isValidGeoJsonPolygon = (
    value: unknown
): value is GeoJsonPolygon => {
    const polygon = value as Partial<GeoJsonPolygon> | undefined;
    return (
        isPlainObject(polygon) &&
        polygon?.type === 'Polygon' &&
        Array.isArray(polygon?.coordinates) &&
        polygon.coordinates.length > 0 &&
        polygon.coordinates.every(isValidGeoJsonLinearRing)
    );
};

/**
 * Sets a promise-based timer
 *
 * @param {number} delay - the number of milliseconds for the timer
 * @example
 * // simulates a 5 second download on click
 * const handleOnClick = async (event: MouseEvent<HTMLButtonElement>) => {
 *     setDownloadStatus('downloading');
 *     await promiseTimer(5000);
 *     setDownloadStatus('downloaded');
 * };
 * @returns {Promise} the promise timer
 */
export const promiseTimer = (delay: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, delay));
};

function truncateString(str: string, num: number): string {
    if (str.length <= num) {
        return str;
    }
    return `${str.slice(0, num)}...`;
}

/**
 * @param str a stringified number to be prefixed with 0s
 * @param size expected length of the returning string padded with starting 0s
 * @returns a padded string with length = size
 */
function getPaddedNDigitNumber(str: string, size: number): string {
    if (str.length >= size) return str;
    return str.padStart(size, '0');
}

/**
 * Returns the number rounded to at most n digits
 *
 * The input value is coersed to a number.
 * When the `value` cannot be coersed, it will return `NaN`.
 * Empty values fallback to zero
 *
 * @param value the number to round
 * @param maxDigitsAfterDecimal the decimal precision
 * @returns
 */
export const roundToMaxDigitsAfterDecimal = (
    value: unknown = 0,
    maxDigitsAfterDecimal = 0
): number => {
    try {
        const parsed = Number.parseFloat(value as string);
        if (Number.isNaN(parsed)) return Number.NaN;

        const digitsAfterDecimal = parsed.toString().split('.')[1] || '';
        const numberOfDigitsAfterDecimal = digitsAfterDecimal.length;

        if (numberOfDigitsAfterDecimal <= maxDigitsAfterDecimal) return parsed;

        return Number(parsed.toFixed(maxDigitsAfterDecimal));
    } catch {
        return Number.NaN;
    }
};

const isDeliveryTaskForEquipment = (
    externalTaskType: string,
    isDelivery: boolean
) => {
    return (
        isDelivery &&
        externalTaskType === constants.externalTaskTypeNames['Empty delivery']
    );
};

export default {
    getCapitalizedFirstChar,
    capitalizeFirstChar,
    getUserName,
    getUserInitials,
    isValidUUID,
    truncateString,
    getPaddedNDigitNumber,
    roundToMaxDigitsAfterDecimal,
    isCurrentHostDemo,
    isCurrentHostUAT,
    isDeliveryTaskForEquipment
};
